import { supabase } from './supabase.js';

export const tasksService = {
  // Get all tasks for a user
  async getTasks(userId, filters = {}) {
    console.log('📋 Getting tasks for user:', userId, 'with filters:', filters);
    
    try {
      let query = supabase
        .from('tasks')
        .select(`
          *,
          created_by_member:family_members!created_by(id, name, avatar, role),
          owner:family_members!owner_id(id, name, avatar, role),
          source_message:family_messages(
            id,
            subject,
            content,
            sender_id,
            sender_name
          )
        `)
        .eq('owner_id', userId);

      // By default, exclude archived tasks unless specifically requested
      if (filters.includeArchived === false || filters.includeArchived === undefined) {
        query = query.is('archived_at', null);
      } else if (filters.archivedOnly) {
        query = query.not('archived_at', 'is', null);
      }

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters.taskType) {
        query = query.eq('task_type', filters.taskType);
      }
      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      if (filters.energyLevel) {
        query = query.eq('energy_level', filters.energyLevel);
      }
      if (filters.overdue) {
        query = query.lt('due_date', new Date().toISOString().split('T')[0])
                     .eq('status', 'pending');
      }
      if (filters.dueToday) {
        const today = new Date().toISOString().split('T')[0];
        query = query.eq('due_date', today);
      }
      if (filters.dueThisWeek) {
        const today = new Date();
        const weekEnd = new Date(today);
        weekEnd.setDate(today.getDate() + 7);
        query = query.gte('due_date', today.toISOString().split('T')[0])
                     .lte('due_date', weekEnd.toISOString().split('T')[0]);
      }

      // Sort by priority and due date
      query = query
        .order('status', { ascending: true }) // Pending first
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      console.log('✅ Tasks retrieved:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Error getting tasks:', error);
      throw error;
    }
  },

  // Create a new task
  async createTask(taskData) {
    console.log('➕ Creating task:', taskData);
    
    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          owner_id: taskData.ownerId,
          title: taskData.title,
          description: taskData.description,
          status: taskData.status || 'pending',
          priority: taskData.priority || 'medium',
          task_type: taskData.taskType || 'manual',
          category: taskData.category,
          energy_level: taskData.energyLevel,
          due_date: taskData.dueDate,
          created_by: taskData.createdBy || taskData.ownerId
        })
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Task created:', data?.id);
      return data;
    } catch (error) {
      console.error('❌ Error creating task:', error);
      throw error;
    }
  },

  // Create brain dump task
  async createBrainDumpTask(userId, content) {
    console.log('🧠 Creating brain dump task');
    
    try {
      const { data, error } = await supabase.rpc('add_brain_dump_task', {
        p_owner_id: userId,
        p_content: content
      });

      if (error) throw error;

      console.log('✅ Brain dump task created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error creating brain dump task:', error);
      throw error;
    }
  },

  // Create quick todo
  async createQuickTodo(userId, title, priority = 'medium') {
    console.log('⚡ Creating quick todo');
    
    try {
      const { data, error } = await supabase.rpc('add_quick_todo', {
        p_owner_id: userId,
        p_title: title,
        p_priority: priority
      });

      if (error) throw error;

      console.log('✅ Quick todo created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error creating quick todo:', error);
      throw error;
    }
  },

  // Update task
  async updateTask(taskId, updates, userId) {
    console.log('✏️ Updating task:', taskId);
    
    try {
      // First get the current task to check if it's a reminder being completed
      let taskBeforeUpdate = null;
      if (updates.status === 'completed') {
        const { data: currentTask, error: fetchError } = await supabase
          .from('tasks')
          .select(`
            *,
            source_message:family_messages(
              id,
              sender_id,
              sender_name
            ),
            owner:family_members!owner_id(
              id,
              name
            )
          `)
          .eq('id', taskId)
          .eq('owner_id', userId)
          .single();
          
        if (fetchError) throw fetchError;
        taskBeforeUpdate = currentTask;
      }

      const updateData = {
        updated_at: new Date().toISOString()
      };

      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.priority !== undefined) updateData.priority = updates.priority;
      if (updates.category !== undefined) updateData.category = updates.category;
      if (updates.energyLevel !== undefined) updateData.energy_level = updates.energyLevel;
      if (updates.dueDate !== undefined) updateData.due_date = updates.dueDate;

      // Handle completion
      if (updates.status === 'completed' && updateData.status !== 'completed') {
        updateData.completed_at = new Date().toISOString();
      } else if (updates.status !== 'completed') {
        updateData.completed_at = null;
      }

      const { data, error } = await supabase
        .from('tasks')
        .update(updateData)
        .eq('id', taskId)
        .eq('owner_id', userId) // Ensure user owns the task
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('Task not found or unauthorized');

      // Send completion notification for reminder tasks
      if (updates.status === 'completed' && taskBeforeUpdate && 
          taskBeforeUpdate.task_type === 'reminder' && 
          taskBeforeUpdate.source_message?.sender_id &&
          taskBeforeUpdate.source_message.sender_id !== userId) {
        
        try {
          // Send a notification message to the original sender
          await supabase.rpc('send_family_message', {
            p_sender_id: userId, // Current user who completed the task
            p_type: 'system',
            p_content: `✅ Completed: "${taskBeforeUpdate.title}"`,
            p_subject: 'Task Completed',
            p_recipient_ids: [taskBeforeUpdate.source_message.sender_id],
            p_priority: 'normal'
          });
          
          console.log('✅ Completion notification sent to:', taskBeforeUpdate.source_message.sender_name);
        } catch (notificationError) {
          console.error('❌ Failed to send completion notification:', notificationError);
          // Don't fail the task update if notification fails
        }
      }

      console.log('✅ Task updated:', taskId);
      return data;
    } catch (error) {
      console.error('❌ Error updating task:', error);
      throw error;
    }
  },

  // Toggle task completion
  async toggleTaskComplete(taskId, userId) {
    console.log('✔️ Toggling task completion:', taskId);
    
    try {
      // First get current status
      const { data: task, error: fetchError } = await supabase
        .from('tasks')
        .select('status')
        .eq('id', taskId)
        .eq('owner_id', userId)
        .single();

      if (fetchError) throw fetchError;
      if (!task) throw new Error('Task not found');

      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      
      return await this.updateTask(taskId, { status: newStatus }, userId);
    } catch (error) {
      console.error('❌ Error toggling task:', error);
      throw error;
    }
  },

  // Delete task
  async deleteTask(taskId, userId) {
    console.log('🗑️ Deleting task:', taskId);
    
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)
        .eq('owner_id', userId);

      if (error) throw error;

      console.log('✅ Task deleted:', taskId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting task:', error);
      throw error;
    }
  },

  // Bulk operations
  async bulkUpdateTasks(taskIds, updates, userId) {
    console.log('📦 Bulk updating tasks:', taskIds.length);
    
    try {
      const updateData = {
        updated_at: new Date().toISOString(),
        ...updates
      };

      const { data, error } = await supabase
        .from('tasks')
        .update(updateData)
        .in('id', taskIds)
        .eq('owner_id', userId)
        .select();

      if (error) throw error;

      console.log('✅ Bulk update completed:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Error bulk updating tasks:', error);
      throw error;
    }
  },

  async bulkDeleteTasks(taskIds, userId) {
    console.log('🗑️ Bulk deleting tasks:', taskIds.length);
    
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .in('id', taskIds)
        .eq('owner_id', userId);

      if (error) throw error;

      console.log('✅ Bulk delete completed');
      return { success: true, count: taskIds.length };
    } catch (error) {
      console.error('❌ Error bulk deleting tasks:', error);
      throw error;
    }
  },

  // Get task statistics
  async getTaskStats(userId) {
    console.log('📊 Getting task statistics for:', userId);
    
    try {
      const { data: tasks, error } = await supabase
        .from('tasks')
        .select('status, priority, task_type, energy_level, due_date')
        .eq('owner_id', userId);

      if (error) throw error;

      const stats = {
        total: tasks.length,
        byStatus: {
          pending: 0,
          in_progress: 0,
          completed: 0,
          cancelled: 0
        },
        byPriority: {
          low: 0,
          medium: 0,
          high: 0
        },
        byType: {
          reminder: 0,
          brain_dump: 0,
          manual: 0,
          quick_add: 0
        },
        overdue: 0,
        dueToday: 0,
        dueThisWeek: 0
      };

      const today = new Date().toISOString().split('T')[0];
      const weekEnd = new Date();
      weekEnd.setDate(weekEnd.getDate() + 7);

      tasks.forEach(task => {
        // Count by status
        if (task.status) stats.byStatus[task.status]++;
        
        // Count by priority
        if (task.priority) stats.byPriority[task.priority]++;
        
        // Count by type
        if (task.task_type) stats.byType[task.task_type]++;
        
        // Count overdue/due dates
        if (task.due_date && task.status !== 'completed') {
          if (task.due_date < today) stats.overdue++;
          if (task.due_date === today) stats.dueToday++;
          if (task.due_date <= weekEnd.toISOString().split('T')[0]) stats.dueThisWeek++;
        }
      });

      console.log('✅ Task statistics calculated:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Error getting task stats:', error);
      throw error;
    }
  },

  // Get categories
  async getCategories(userId) {
    console.log('🏷️ Getting task categories for:', userId);
    
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('category')
        .eq('owner_id', userId)
        .not('category', 'is', null);

      if (error) throw error;

      // Extract unique categories
      const categories = [...new Set(data.map(t => t.category))].filter(Boolean);

      console.log('✅ Categories retrieved:', categories);
      return categories;
    } catch (error) {
      console.error('❌ Error getting categories:', error);
      throw error;
    }
  },

  // Subscribe to task changes
  subscribeToTasks(userId, callback) {
    console.log('🔔 Subscribing to tasks for:', userId);
    
    const channelId = `tasks-${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const subscription = supabase
      .channel(channelId)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'tasks',
          filter: `owner_id=eq.${userId}`
        }, 
        (payload) => {
          console.log('🔄 Task change:', payload);
          callback(payload);
        }
      )
      .subscribe();
    
    return subscription;
  },

  unsubscribe(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  },

  // Archive operations
  async archiveTask(taskId, userId) {
    console.log('📦 Archiving task:', taskId);
    
    try {
      const { data, error } = await supabase.rpc('archive_task', {
        p_task_id: taskId,
        p_user_id: userId
      });

      if (error) throw error;

      console.log('✅ Task archived:', taskId);
      return data;
    } catch (error) {
      console.error('❌ Error archiving task:', error);
      throw error;
    }
  },

  async unarchiveTask(taskId, userId) {
    console.log('📤 Unarchiving task:', taskId);
    
    try {
      const { data, error } = await supabase.rpc('unarchive_task', {
        p_task_id: taskId,
        p_user_id: userId
      });

      if (error) throw error;

      console.log('✅ Task unarchived:', taskId);
      return data;
    } catch (error) {
      console.error('❌ Error unarchiving task:', error);
      throw error;
    }
  },

  async runAutoArchive(userId) {
    console.log('🔄 Running auto-archive for user:', userId);
    
    try {
      const { data, error } = await supabase.rpc('archive_completed_tasks', {
        p_user_id: userId
      });

      if (error) throw error;

      console.log('✅ Auto-archived tasks:', data);
      return data;
    } catch (error) {
      console.error('❌ Error running auto-archive:', error);
      throw error;
    }
  },

  // User preferences
  async getTaskPreferences(userId) {
    console.log('⚙️ Getting task preferences for:', userId);
    
    try {
      const { data, error } = await supabase
        .from('user_task_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        // No preferences found, return defaults
        return {
          user_id: userId,
          auto_archive_enabled: true,
          auto_archive_days: 7
        };
      }

      if (error) throw error;

      console.log('✅ Preferences retrieved:', data);
      return data;
    } catch (error) {
      console.error('❌ Error getting preferences:', error);
      throw error;
    }
  },

  async updateTaskPreferences(userId, preferences) {
    console.log('⚙️ Updating task preferences:', preferences);
    
    try {
      const { data, error } = await supabase
        .from('user_task_preferences')
        .upsert({
          user_id: userId,
          auto_archive_enabled: preferences.autoArchiveEnabled,
          auto_archive_days: preferences.autoArchiveDays,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Preferences updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Error updating preferences:', error);
      throw error;
    }
  }
};